'use client';

import { logoutUser } from '@/services/userSessionService';
import { selectIsLoggedIn, useUserSessionStore } from '@/stores/userSessionStore';
import { Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

export function Footer() {
  const router = useRouter();
  const isLoggedIn = useUserSessionStore(selectIsLoggedIn);

  const handleLogout = () => {
    router.push('/');
    logoutUser();
  };

  return (
    <footer className="bg-[#1C2B3A] text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8 md:justify-items-center md:items-start">
          <div className="w-full max-w-xs">
            <h3 className="font-bold text-lg mb-4">About WarrantyPilot</h3>
            <p className="text-gray-300 mb-4">
              Empowering car owners with transparent and flexible vehicle service contracts.
            </p>
            <div className="flex space-x-4 mt-4">
              <a
                href="https://x.com/WarrantyPilot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="X (Twitter)"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://facebook.com/warrantypilot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://instagram.com/warrantypilot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://linkedin.com/company/warrantypilot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="https://tiktok.com/@warrantypilot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="TikTok"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                >
                  <path d="M9 12a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />
                  <path d="M15 8a4 4 0 0 0 4 4V8a4 4 0 0 1-4-4" />
                  <path d="M15 8v8a4 4 0 0 1-4 4" />
                </svg>
              </a>
            </div>
          </div>
          <div className="w-full max-w-xs">
            <h3 className="font-bold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2 text-gray-300">
              <li>
                <Link href="/" className="text-base hover:text-white">
                  Home
                </Link>
              </li>
              <li>
                <Link href="/partner" className="text-base hover:text-white">
                  Become a Partner
                </Link>
              </li>
              <li>
                <Link href="/blog" className="text-base hover:text-white">
                  Blog
                </Link>
              </li>
            </ul>
          </div>
          <div className="w-full max-w-xs">
            <h3 className="font-bold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-2 text-gray-300">
              <li>Email: {clientConfig.NEXT_PUBLIC_WP_SUPPORT_EMAIL}</li>
              <li>Phone: {clientConfig.NEXT_PUBLIC_WP_SUPPORT_PHONE_NUMBER}</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-700 pt-8 text-sm text-gray-400 space-y-4">
          <p className="mb-4">
            WarrantyPilot Inc. markets vehicle service contracts on behalf of 3rd party administrators. A Vehicle
            Service Contract (VSC), commonly called an "auto warranty" or an "extended car warranty," is not a warranty.
            Instead, it offers repair coverage for your vehicle after the manufacturer's warranty ends. A VSC is a
            contract between you and a VSC provider or administrator, detailing which repairs are covered and which are
            not. Please note that not all vehicles qualify for coverage.
          </p>
          <div className="flex flex-wrap justify-between items-center">
            <p>© {new Date().getFullYear()} WarrantyPilot Inc. All rights reserved.</p>
            <div className="space-x-4">
              <Link href="/privacy" className="hover:text-white">
                Privacy Policy
              </Link>
              <Link href="/terms" className="hover:text-white">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
